import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  ClockIcon,
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/outline";
import * as API from "../../api";

/**
 * Contact — redesigned to match the Tailwind design system introduced on
 * Home/Footer (rounded-pill inputs with icon prefixes, brand/accent colors,
 * font-display headings, shadow-card/elevated).
 *
 * Submission logic is unchanged from the legacy MUI page: same API.Contact
 * call, same field names (full_name, email, phone, message), same 422/401/500
 * handling — only the JSX/styling was rewritten.
 */

function FieldError({ message }) {
  if (!message) return null;
  return (
    <p className="mt-1.5 flex items-center gap-1 text-xs font-medium text-red-600">
      <ExclamationCircleIcon className="h-3.5 w-3.5 shrink-0" />
      {message}
    </p>
  );
}

function TextField({ icon: Icon, error, className = "", ...props }) {
  return (
    <div
      className={`flex items-center gap-2.5 rounded-full border bg-white ps-4 pe-4 h-12 transition ${
        error
          ? "border-red-300 focus-within:border-red-400 focus-within:ring-2 focus-within:ring-red-400/15"
          : "border-slate-200 hover:border-slate-300 focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/15"
      } ${className}`}
    >
      <Icon className="h-3.5 w-3.5 shrink-0 text-ink-subtle" />
      <input
        className="min-w-0 flex-1 bg-transparent text-sm font-medium text-ink placeholder:text-ink-subtle outline-none"
        {...props}
      />
    </div>
  );
}

export default function Contact() {
  const { t } = useTranslation();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(false);
  const [nameHelper, setNameHelper] = useState("");
  const [emailHelper, setEmailHelper] = useState("");
  const [phoneHelper, setPhoneHelper] = useState("");
  const [messageHelper, setMessageHelper] = useState("");

  const [contactHelper, setContactHelper] = useState({
    type: "",
    message: "",
  });

  useEffect(() => {
    let alive = true;
    API.Settings()
      .then((res) => alive && setSettings(res?.settings || {}))
      .catch(() => alive && setSettings({}));
    return () => {
      alive = false;
    };
  }, []);

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const validate = (formData) => {
    const errors = {};
    const name = (formData.get("full_name") || "").toString().trim();
    const email = (formData.get("email") || "").toString().trim();
    const message = (formData.get("message") || "").toString().trim();

    if (!name) {
      errors.name = t("contact.errorNameRequired", "Merci d'indiquer votre nom.");
    }
    if (!email) {
      errors.email = t("contact.errorEmailRequired", "Merci d'indiquer votre email.");
    } else if (!EMAIL_RE.test(email)) {
      errors.email = t("contact.errorEmailInvalid", "Cet email ne semble pas valide.");
    }
    if (!message) {
      errors.message = t("contact.errorMessageRequired", "Merci d'écrire un message.");
    }
    return errors;
  };

  const sendMessage = (event) => {
    event.preventDefault();
    const formData = new FormData(event.target);

    setContactHelper({ type: "", message: "" });
    setNameHelper("");
    setEmailHelper("");
    setPhoneHelper("");
    setMessageHelper("");

    const errors = validate(formData);
    if (Object.keys(errors).length) {
      if (errors.name) setNameHelper(errors.name);
      if (errors.email) setEmailHelper(errors.email);
      if (errors.message) setMessageHelper(errors.message);
      return;
    }

    setLoading(true);
    API.Contact(formData)
      .then(() => {
        setContactHelper({
          type: "success",
          message: t("contact.formSuccessDefault", "Votre message a été envoyé avec succès."),
        });
        event.target.reset();
      })
      .catch((err) => {
        // Validation
        if (err.status === 422) {
          setContactHelper({ type: "error", message: err.data.message });
          if (err.data.errors.full_name) {
            setNameHelper(err.data.errors.full_name);
          }
          if (err.data.errors.email) {
            setEmailHelper(err.data.errors.email);
          }
          if (err.data.errors.phone) {
            setPhoneHelper(err.data.errors.phone);
          }
        }
        // Unauthorized or user not found
        if (err.status === 401) {
          setContactHelper({ type: "error", message: err.data.errors });
        }
        // Server error
        if (err.status === 500) {
          setContactHelper({
            type: "error",
            message: t("contact.formErrorServer", "Erreur du serveur"),
          });
        }
      })
      .then(() => {
        setLoading(false);
      });
  };

  const infoRows = [
    settings.email
      ? { icon: EnvelopeIcon, label: t("contact.infoEmail", "Email"), value: settings.email, href: `mailto:${settings.email}` }
      : null,
    settings.phone
      ? { icon: PhoneIcon, label: t("contact.infoPhone", "Téléphone"), value: settings.phone, href: `tel:${settings.phone}`, dir: "ltr" }
      : null,
    settings.address
      ? { icon: MapPinIcon, label: t("contact.infoAddress", "Adresse"), value: settings.address }
      : null,
  ].filter(Boolean);

  return (
    <div className="bg-surface">
      {/* Hero — mirrors Home hero's copy structure (eyebrow badge + 2-line title) */}
      <section className="relative overflow-hidden bg-surface-muted">
        <div className="mx-auto w-full max-w-container px-4 sm:px-6 lg:px-8 pt-10 sm:pt-14 lg:pt-16 pb-10 sm:pb-14">
          <div className="mx-auto max-w-2xl text-center">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-xs font-semibold uppercase tracking-wide text-brand-700 shadow-card border border-slate-200/80">
              {t("contact.eyebrow", "Nous sommes à votre écoute")}
            </span>
            <h1 className="mt-4 font-display font-extrabold text-ink leading-[1.15] tracking-tight text-[1.75rem] sm:text-4xl lg:text-5xl">
              <span className="block text-balance">{t("contact.heroTitle", "Une question ?")}</span>
              <span className="inline-block text-brand-500 mt-1.5">
                {t("contact.heroTitleAccent", "Parlons-en.")}
              </span>
            </h1>
            <p className="mt-4 text-sm sm:text-base text-ink-muted leading-relaxed">
              {t(
                "contact.heroSubtitle",
                "Notre équipe vous répond sous 24h, du lundi au samedi. Que ce soit pour une réservation, un partenariat ou une simple question, on est là."
              )}
            </p>
          </div>
        </div>
      </section>

      {/* Content — info sidebar + form */}
      <section className="relative py-10 sm:py-14 lg:py-16">
        <div className="mx-auto w-full max-w-container px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[0.85fr_1.15fr] lg:gap-10">
            {/* Info card */}
            <div className="order-2 lg:order-1">
              <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-card sm:p-7">
                <h2 className="font-display text-lg font-semibold text-ink sm:text-xl">
                  {t("contact.infoTitle", "Nos coordonnées")}
                </h2>

                <ul className="mt-5 space-y-4">
                  {infoRows.map((row) => {
                    const Icon = row.icon;
                    return (
                      <li key={row.label} className="flex items-start gap-3">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-500">
                          <Icon className="h-4 w-4" />
                        </span>
                        <div className="min-w-0">
                          <p className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">
                            {row.label}
                          </p>
                          {row.href ? (
                            <a
                              href={row.href}
                              dir={row.dir}
                              className="mt-0.5 block truncate text-sm font-medium text-ink hover:text-brand-500 transition-colors"
                            >
                              {row.value}
                            </a>
                          ) : (
                            <p className="mt-0.5 text-sm font-medium text-ink">{row.value}</p>
                          )}
                        </div>
                      </li>
                    );
                  })}

                  <li className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent-500/15 text-accent-700">
                      <ClockIcon className="h-4 w-4" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">
                        {t("contact.infoHoursTitle", "Horaires")}
                      </p>
                      <p className="mt-0.5 text-sm font-medium text-ink">
                        {t("contact.infoHours", "Lun – Sam, 9h – 18h")}
                      </p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            {/* Form card */}
            <div className="order-1 lg:order-2">
              <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-card sm:p-8">
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-brand-50 text-brand-500">
                    <ChatBubbleLeftRightIcon className="h-5 w-5" />
                  </span>
                  <div>
                    <h2 className="font-display text-lg font-semibold text-ink sm:text-xl">
                      {t("contact.formTitle", "Envoyez-nous un message")}
                    </h2>
                    <p className="text-sm text-ink-muted">
                      {t("contact.formSubtitle", "Remplissez le formulaire ci-dessous, nous revenons vers vous rapidement.")}
                    </p>
                  </div>
                </div>

                {contactHelper.message ? (
                  <div
                    className={`mt-5 flex items-start gap-2.5 rounded-xl border px-4 py-3 text-sm font-medium ${
                      contactHelper.type === "success"
                        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                        : "border-red-200 bg-red-50 text-red-700"
                    }`}
                  >
                    {contactHelper.type === "success" ? (
                      <CheckCircleIcon className="mt-0.5 h-4.5 w-4.5 shrink-0" />
                    ) : (
                      <ExclamationCircleIcon className="mt-0.5 h-4.5 w-4.5 shrink-0" />
                    )}
                    <span dangerouslySetInnerHTML={{ __html: contactHelper.message }} />
                  </div>
                ) : null}

                <form onSubmit={sendMessage} method="POST" className="mt-6 space-y-4">
                  <div>
                    <label htmlFor="contact-name" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-ink-subtle">
                      {t("contact.formName", "Nom et prénom")}
                    </label>
                    <TextField
                      id="contact-name"
                      icon={UserIcon}
                      name="full_name"
                      placeholder={t("contact.formNamePlaceholder", "Votre nom complet")}
                      error={nameHelper}
                    />
                    <FieldError message={nameHelper} />
                  </div>

                  <div>
                    <label htmlFor="contact-email" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-ink-subtle">
                      {t("contact.formEmail", "Email")}
                    </label>
                    <TextField
                      id="contact-email"
                      icon={EnvelopeIcon}
                      type="email"
                      name="email"
                      placeholder={t("contact.formEmailPlaceholder", "vous@exemple.com")}
                      error={emailHelper}
                    />
                    <FieldError message={emailHelper} />
                  </div>

                  <div>
                    <label htmlFor="contact-phone" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-ink-subtle">
                      {t("contact.formPhone", "Numéro de téléphone")}
                    </label>
                    <TextField
                      id="contact-phone"
                      icon={PhoneIcon}
                      type="tel"
                      name="phone"
                      placeholder={t("contact.formPhonePlaceholder", "+212 6 00 00 00 00")}
                      error={phoneHelper}
                    />
                    <FieldError message={phoneHelper} />
                  </div>

                  <div>
                    <label htmlFor="contact-message" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-ink-subtle">
                      {t("contact.formMessage", "Message")}
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      rows={4}
                      placeholder={t("contact.formMessagePlaceholder", "Comment pouvons-nous vous aider ?")}
                      className={`w-full rounded-2xl border bg-white px-4 py-3 text-sm font-medium text-ink placeholder:text-ink-subtle outline-none transition resize-none ${
                        messageHelper
                          ? "border-red-300 focus:border-red-400 focus:ring-2 focus:ring-red-400/15"
                          : "border-slate-200 hover:border-slate-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/15"
                      }`}
                    />
                    <FieldError message={messageHelper} />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="mt-2 flex h-12 w-full items-center justify-center gap-2 rounded-full bg-brand-500 px-6 font-semibold text-white shadow-card transition-colors hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30"
                  >
                    {loading ? (
                      <>
                        <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                        <span>{t("contact.formSubmitting", "Envoi en cours…")}</span>
                      </>
                    ) : (
                      <>
                        <PaperAirplaneIcon className="h-4 w-4 rtl:-scale-x-100" />
                        <span>{t("contact.formSubmit", "Envoyer le message")}</span>
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
